import * as React from 'react';
import PropTypes from 'prop-types';
import { useInRouterContext, useNavigate } from 'react-router';

/**
 * Where a type chip sends the user: the tree, filtered to that item type.
 * @param {string} typeId
 * @returns {string}
 */
export const typeFilterUrl = (typeId) => `/?type=${encodeURIComponent(typeId)}`;

/**
 * Tooltip text for a clickable type chip.
 * @param {string} label
 * @returns {string}
 */
export const typeChipTitle = (label) => `Show all ${label} items in the tree`;

const WRAP_STYLE = { cursor: 'pointer', display: 'inline-flex' };

function ChipShell({ typeId, label, go, chipKey, children }) {
  const open = () => go(typeFilterUrl(typeId));
  return (
    <span
      role="link"
      tabIndex={0}
      style={WRAP_STYLE}
      data-testid="type-chip-link"
      data-chip-key={chipKey}
      title={typeChipTitle(label)}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
    >
      {children}
    </span>
  );
}

const shellPropTypes = {
  typeId: PropTypes.string,
  label: PropTypes.string,
  go: PropTypes.func,
  chipKey: PropTypes.string,
  children: PropTypes.node,
};
ChipShell.propTypes = shellPropTypes;

function RouterChip({ typeId, label, chipKey, children }) {
  const navigate = useNavigate();
  return <ChipShell typeId={typeId} label={label} go={navigate} chipKey={chipKey}>{children}</ChipShell>;
}
RouterChip.propTypes = shellPropTypes;

const hardNavigate = (url) => { window.location.assign(url); };

/**
 * Wraps an item-type chip so a click opens the tree filtered to that type (#1092).
 * Without a type id it renders the chip unchanged. Admin item-type pages do not use it.
 * `chipKey` goes on the wrapper as `data-chip-key`. The item header's chip-fit hook
 * counts every row child without a known key as fixed width, so an unkeyed wrapper
 * counted the chip twice, and the chip moved in and out of the row without end.
 * @param {{typeId?: string|null, label: string, chipKey?: string, children?: React.ReactNode}} props
 */
export default function TypeChipLink({ typeId, label, chipKey, children }) {
  const inRouter = useInRouterContext();
  if (!typeId) return children ?? null;
  if (inRouter) return <RouterChip typeId={typeId} label={label} chipKey={chipKey}>{children}</RouterChip>;
  return <ChipShell typeId={typeId} label={label} go={hardNavigate} chipKey={chipKey}>{children}</ChipShell>;
}

TypeChipLink.propTypes = {
  typeId: PropTypes.string, label: PropTypes.string, chipKey: PropTypes.string, children: PropTypes.node,
};
